import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Card, Typography, Chip, Avatar, Button, CircularProgress,
    Table, TableBody, TableCell, TableRow, TableHead, TableContainer,
    Grid, Alert, Breadcrumbs, Link as MuiLink, Tabs, Tab,
    Accordion, AccordionSummary, AccordionDetails, IconButton, Tooltip, Skeleton,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
    FormControlLabel, Switch
} from '@mui/material';
import { useNotify } from 'react-admin';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import {
    Person as PersonIcon, Group as GroupIcon, Email as EmailIcon,
    CheckCircle as CheckCircleIcon, Cancel as CancelIcon,
    Info as InfoIcon, CalendarToday as CalendarTodayIcon,
    Payment as PaymentIcon, Refresh as RefreshIcon,
    CheckCircle as SuccessIcon, HourglassEmpty as PendingIcon,
    Error as FailedIcon, MoneyOff as RefundedIcon,
    ExpandMore as ExpandMoreIcon, ContentCopy as ContentCopyIcon,
    Star as StarIcon, SportsSoccer as SportsSoccerIcon,
    Groups as GroupsIcon, DeleteForever as DeleteForeverIcon
} from '@mui/icons-material';

import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../../config/config.json';
import { UsersGroupSettingsDialog } from './UsersGroupSettingsDialog';

const host = config.production.host;

const getJwt = () => localStorage.getItem("feathers-jwt");
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const formatDateForInput = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatDateTimeForInput = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const addDays = (value, days) => {
    const d = new Date(value);
    d.setDate(d.getDate() + days);
    return d;
};

const extractGroupId = (group) => {
    if (!group) return null;
    const candidates = [group.tbl_gruppoId, group.gruppoId, group.id];
    for (const candidate of candidates) {
        const parsed = parseInt(candidate, 10);
        if (Number.isInteger(parsed)) return parsed;
    }
    return null;
};

const getUserGroupIds = (groups) => {
    if (!Array.isArray(groups)) return [];
    const ids = groups
        .map((g) => extractGroupId(g))
        .filter((id) => Number.isInteger(id));
    return [...new Set(ids)];
};

const isPlanAllowedForUserGroups = (plan, userGroupIds) => {
    if (!plan) return false;
    if (!userGroupIds.length) return true;
    const planGroupId = parseInt(plan.gruppoId, 10);
    if (!Number.isInteger(planGroupId)) return true; // piano globale senza gruppo
    return userGroupIds.includes(planGroupId);
};

const ROLE_COLORS = {
    admin: '#e85a2c',
    user: '#2196f3',
    guest: '#9e9e9e',
    moderator: '#ff9800',
    editor: '#4caf50',
    candidato: '#9c27b0',
    collaboratore: '#00bcd4'
};

const ROLE_LABELS = {
    admin: 'Amministratore',
    user: 'Utente',
    guest: 'Ospite',
    moderator: 'Moderatore',
    editor: 'Editor',
    candidato: 'Candidato',
    collaboratore: 'Collaboratore'
};

const getInitials = (nome, cognome) => {
    const n = nome ? nome.charAt(0).toUpperCase() : '';
    const c = cognome ? cognome.charAt(0).toUpperCase() : '';
    return `${n}${c}` || '?';
};

// Stripe transaction status colors and labels
const STRIPE_STATUS_COLORS = {
    pending: '#ff9800',
    succeeded: '#4caf50',
    failed: '#f44336',
    canceled: '#9e9e9e',
    refunded: '#2196f3'
};

const STRIPE_STATUS_ICONS = {
    pending: PendingIcon,
    succeeded: CheckCircleIcon,
    failed: FailedIcon,
    canceled: CancelIcon,
    refunded: RefundedIcon
};

const STRIPE_STATUS_LABELS = {
    pending: 'In Attesa',
    succeeded: 'Completato',
    failed: 'Fallito',
    canceled: 'Annullato',
    refunded: 'Rimborsato'
};

export const UsersDetailPage = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const notify = useNotify();

    // Stati
    const [user, setUser] = React.useState(null);
    const [gruppi, setGruppi] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [loadingGruppi, setLoadingGruppi] = React.useState(false);
    const [groupSettings, setGroupSettings] = React.useState({});
    const [activeTab, setActiveTab] = React.useState(0);
    const [transactions, setTransactions] = React.useState([]);
    const [loadingTransactions, setLoadingTransactions] = React.useState(false);
    const [subscription, setSubscription] = React.useState(null);
    const [subscriptionHistory, setSubscriptionHistory] = React.useState([]);
    const [loadingSubscription, setLoadingSubscription] = React.useState(false);
    const [postStats, setPostStats] = React.useState(null);
    const [loadingPostStats, setLoadingPostStats] = React.useState(false);
    const [deletionRequest, setDeletionRequest] = React.useState(null);
    const [debugDialog, setDebugDialog] = React.useState({ open: false, data: {} });
    const [allAvailablePlans, setAllAvailablePlans] = React.useState([]);
    const [loadingAvailablePlans, setLoadingAvailablePlans] = React.useState(false);
    const [subscriptionDialog, setSubscriptionDialog] = React.useState({
        open: false,
        mode: 'create', // create | edit
        loading: false
    });
    const [subscriptionForm, setSubscriptionForm] = React.useState({
        pianoId: '',
        durataType: 'mensile',
        dataInizio: '',
        dataScadenza: '',
        note: '',
        cancel_at_period_end: false
    });
    const [durataPreset, setDurataPreset] = React.useState(null);
    const [cancelDialogOpen, setCancelDialogOpen] = React.useState(false);

    // Group Settings Dialog State
    const [openSettingsDialog, setOpenSettingsDialog] = React.useState(false);
    const [selectedSettings, setSelectedSettings] = React.useState(null);

    const loadUser = async () => {
        loadUserDetails();
    };

    const handleOpenDebug = (data) => {
        setDebugDialog({ open: true, data });
    };

    const handleCloseDebug = () => {
        setDebugDialog({ open: false, data: {} });
    };

    const handleOpenSettings = (e, settings) => {
        e.stopPropagation(); // Prevent accordion expansion
        setSelectedSettings(settings);
        setOpenSettingsDialog(true);
    };

    const handleCloseSettings = () => {
        setOpenSettingsDialog(false);
        setSelectedSettings(null);
    };

    const handleSaveSettings = () => {
        loadUser(); // Reload user data to reflect changes
    };

    const loadAvailableSubscriptionPlans = async () => {
        try {
            setLoadingAvailablePlans(true);
            const jwt = getJwt();
            const response = await axios.get(`${host}/tbl-piani`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: {
                    flAttivo: 1,
                    flVisibile: 1,
                    $limit: 200,
                    $sort: { nome: 1 }
                }
            });
            const plans = response.data?.data || response.data || [];
            setAllAvailablePlans(plans);
            return plans;
        } catch (error) {
            console.error('Errore nel recupero piani disponibili:', error);
            notify('Errore nel recupero piani disponibili', { type: 'error' });
            setAllAvailablePlans([]);
            return [];
        } finally {
            setLoadingAvailablePlans(false);
        }
    };

    const openCreateSubscriptionDialog = async () => {
        const allPlans = allAvailablePlans.length ? allAvailablePlans : await loadAvailableSubscriptionPlans();
        const allowedPlans = allPlans.filter((plan) => isPlanAllowedForUserGroups(plan, getUserGroupIds(gruppi)));
        if (!allowedPlans.length) {
            notify('Nessun piano disponibile per i gruppi di appartenenza dell’utente', { type: 'warning' });
            return;
        }

        const defaultPiano = allowedPlans[0]?.id || '';
        const now = new Date();
        const nextMonth = new Date(now);
        nextMonth.setMonth(nextMonth.getMonth() + 1);

        setDurataPreset(1);
        setSubscriptionForm({
            pianoId: defaultPiano,
            durataType: 'mensile',
            dataInizio: formatDateTimeForInput(now),
            dataScadenza: formatDateForInput(nextMonth),
            note: ''
        });
        setSubscriptionDialog({ open: true, mode: 'create', loading: false });
    };

    const openEditSubscriptionDialog = async () => {
        if (!subscription) return;
        if (!allAvailablePlans.length) await loadAvailableSubscriptionPlans();

        setDurataPreset(null);
        setSubscriptionForm({
            pianoId: subscription.pianoId || '',
            durataType: subscription.durataType || 'mensile',
            dataInizio: formatDateForInput(subscription.dataInizio),
            dataScadenza: formatDateForInput(subscription.dataScadenza),
            note: subscription.note || '',
            cancel_at_period_end: !subscription.autoRinnovo
        });
        setSubscriptionDialog({ open: true, mode: 'edit', loading: false });
    };

    const closeSubscriptionDialog = () => {
        setDurataPreset(null);
        setSubscriptionDialog((prev) => ({ ...prev, open: false, loading: false }));
    };

    const handleSubscriptionFormChange = (field, value) => {
        setSubscriptionForm((prev) => {
            const updated = { ...prev, [field]: value };
            if (field === 'dataInizio' && durataPreset) {
                const inizio = new Date(value);
                if (!isNaN(inizio.getTime())) {
                    const scadenza = new Date(inizio);
                    scadenza.setMonth(scadenza.getMonth() + durataPreset);
                    updated.dataScadenza = formatDateForInput(scadenza);
                }
            }
            return updated;
        });
    };

    const DURATA_PRESETS = [
        { label: '1 mese', months: 1 },
        { label: '2 mesi', months: 2 },
        { label: '3 mesi', months: 3 },
        { label: '6 mesi', months: 6 },
        { label: '1 anno', months: 12 },
    ];

    const applyDurataPreset = (months) => {
        setDurataPreset(months);
        const inizio = subscriptionForm.dataInizio ? new Date(subscriptionForm.dataInizio) : new Date();
        const scadenza = new Date(inizio);
        scadenza.setMonth(scadenza.getMonth() + months);
        setSubscriptionForm((prev) => ({ ...prev, dataScadenza: formatDateForInput(scadenza) }));
    };

    const applyTrialPreset = (days) => {
        const now = new Date();
        const end = addDays(now, days);
        setSubscriptionForm((prev) => ({
            ...prev,
            dataInizio: formatDateTimeForInput(now),
            dataScadenza: formatDateForInput(end),
            note: prev.note || `Prova gratuita ${days} giorni`
        }));
    };

    const filteredPlansForSubscription = React.useMemo(() => {
        const allowed = allAvailablePlans.filter((plan) => isPlanAllowedForUserGroups(plan, getUserGroupIds(gruppi)));
        if (!subscriptionForm.pianoId) return allowed;
        const currentId = parseInt(subscriptionForm.pianoId, 10);
        const alreadyIncluded = allowed.some((p) => parseInt(p.id, 10) === currentId);
        if (alreadyIncluded) return allowed;
        const currentPlan = allAvailablePlans.find((p) => parseInt(p.id, 10) === currentId);
        return currentPlan ? [currentPlan, ...allowed] : allowed;
    }, [allAvailablePlans, gruppi, subscriptionForm.pianoId]);

    const submitSubscriptionForm = async () => {
        if (!userId) return;
        if (!subscriptionForm.pianoId || !subscriptionForm.durataType || !subscriptionForm.dataInizio) {
            notify('Compila i campi obbligatori (Piano, Durata, Data inizio)', { type: 'warning' });
            return;
        }

        try {
            setSubscriptionDialog((prev) => ({ ...prev, loading: true }));
            const jwt = getJwt();
            const payload = {
                pianoId: parseInt(subscriptionForm.pianoId, 10),
                durataType: subscriptionForm.durataType,
                dataInizio: subscriptionForm.dataInizio,
                dataScadenza: subscriptionForm.dataScadenza || null,
                note: subscriptionForm.note || null,
                cancel_at_period_end: subscriptionForm.cancel_at_period_end
            };

            if (subscriptionDialog.mode === 'create') {
                const selectedPlan = allAvailablePlans.find((plan) => parseInt(plan.id, 10) === payload.pianoId);
                const preferredGroup = gruppi.find((g) => !!g.flPrincipale) || gruppi[0];
                const resolvedGroupId = Number.isInteger(parseInt(selectedPlan?.gruppoId, 10))
                    ? parseInt(selectedPlan.gruppoId, 10)
                    : extractGroupId(preferredGroup);

                if (!Number.isInteger(resolvedGroupId)) {
                    throw new Error('Impossibile determinare il gruppo per la creazione abbonamento');
                }

                const procResponse = await axios.get(`${host}/dynamic-proc`, {
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: {
                        proc_name: 'proc_crea_users_piano',
                        in_userId: parseInt(userId, 10),
                        in_pianoId: payload.pianoId,
                        in_gruppoId: resolvedGroupId,
                        in_durataType: payload.durataType,
                        in_dataInizio: payload.dataInizio
                    }
                });

                const out = procResponse?.data?.output_params || {};
                if (Number(out.out_esito) !== 1) {
                    throw new Error(out.out_msg || 'Errore procedura proc_crea_users_piano');
                }

                // Non modifichiamo dynamic-proc: trigger sync Stripe passando dal service tbl-users-piano.
                const createdRows = await axios.get(`${host}/tbl-users-piano`, {
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: {
                        usersId: parseInt(userId, 10),
                        status: 'active',
                        $sort: { createdAt: -1 },
                        $limit: 1
                    }
                });
                const createdSub = createdRows.data?.data?.[0] || createdRows.data?.[0];
                if (createdSub?.id) {
                    await axios.patch(`${host}/tbl-users-piano/${createdSub.id}`, {
                        pianoId: payload.pianoId,
                        durataType: payload.durataType
                    }, {
                        headers: { 'Authorization': `Bearer ${jwt}` }
                    });
                }
                notify('Abbonamento assegnato correttamente', { type: 'success' });
            } else {
                if (!subscription?.id) {
                    throw new Error('Abbonamento attivo non trovato');
                }
                await axios.patch(`${host}/tbl-users-piano/${subscription.id}`, payload, {
                    headers: { 'Authorization': `Bearer ${jwt}` }
                });
                notify('Abbonamento modificato correttamente', { type: 'success' });
            }

            closeSubscriptionDialog();
            await loadUserSubscription(userId);
            await loadUserPostStats(userId);
        } catch (error) {
            console.error('Errore salvataggio abbonamento:', error);
            notify(error.response?.data?.message || 'Errore nel salvataggio abbonamento', { type: 'error' });
            setSubscriptionDialog((prev) => ({ ...prev, loading: false }));
        }
    };

    const handleCancelSubscription = async () => {
        if (!subscription?.id || !userId) return;

        try {
            const jwt = getJwt();
            await axios.patch(`${host}/tbl-users-piano/${subscription.id}`, {
                status: 'canceled'
            }, {
                headers: { 'Authorization': `Bearer ${jwt}` }
            });
            notify('Abbonamento cancellato correttamente', { type: 'success' });
            setCancelDialogOpen(false);
            await loadUserSubscription(userId);
            await loadUserPostStats(userId);
        } catch (error) {
            console.error('Errore cancellazione abbonamento:', error);
            notify(error.response?.data?.message || 'Errore nella cancellazione abbonamento', { type: 'error' });
        }
    };

    // Funzione per copiare negli appunti
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        notify('Copiato negli appunti!', { type: 'success' });
    };
    const loadUserDetails = async () => {
        try {
            setLoading(true);
            setError(null);
            const jwt = getJwt();

            const response = await axios.get(
                `${host}/users/${userId}`,
                {
                    headers: { 'Authorization': `Bearer ${jwt}` }
                }
            );

            setUser(response.data);

            // Controlla se esiste una richiesta di cancellazione pendente
            try {
                const delReqResp = await axios.get(`${host}/tbl-delete-requests`, {
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: { email: response.data.email, status: 'pending', $limit: 1 }
                });
                const delReqs = delReqResp.data?.data || delReqResp.data || [];
                setDeletionRequest(delReqs.length > 0 ? delReqs[0] : null);
            } catch (e) {
                setDeletionRequest(null);
            }

            // Carica i gruppi solo se non è admin
            if (response.data.roles !== 'admin') {
                await loadUserGruppi(userId);
            } else {
                setGruppi([]);
            }

            // Carica abbonamento
            await loadUserSubscription(userId);

        } catch (error) {
            console.error("Errore nel recupero dettagli utente:", error);
            setError("Errore nel caricamento dei dettagli dell'utente");
            notify("Errore nel caricamento dei dettagli", { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Carica i gruppi dell'utente e i relativi settings
    const loadUserGruppi = async (uid) => {
        try {
            setLoadingGruppi(true);
            const jwt = getJwt();
            let groupsWithMain = [];

            for (let attempt = 1; attempt <= 3; attempt += 1) {
                // 1. Dettaglio gruppi (proc)
                let groupsData = [];
                try {
                    const procResponse = await axios.get(`${host}/proc-get-users-gruppi`, {
                        headers: { 'Authorization': `Bearer ${jwt}` },
                        params: { p_input_usersId: uid }
                    });
                    groupsData = procResponse.data?.data || [];
                } catch (procError) {
                    console.error("Errore proc-get-users-gruppi:", procError);
                    throw procError;
                }

                // 2. Relazioni dirette
                let relData = [];
                try {
                    const relResponse = await axios.get(`${host}/tbl-users-gruppo`, {
                        headers: { 'Authorization': `Bearer ${jwt}` },
                        params: { usersId: parseInt(uid, 10) }
                    });
                    relData = relResponse.data?.data || [];
                } catch (relError) {
                    console.warn("Errore tbl-users-gruppo (ignoring):", relError);
                }

                // Fallback: se proc è vuota ma relazioni esistono, costruisci dati base da tbl-gruppo
                if ((!Array.isArray(groupsData) || groupsData.length === 0) && relData.length > 0) {
                    const groupIds = [...new Set(relData
                        .map((r) => parseInt(r.tbl_gruppoId, 10))
                        .filter((id) => Number.isInteger(id)))];
                    if (groupIds.length > 0) {
                        try {
                            const groupsResponse = await axios.get(`${host}/tbl-gruppo`, {
                                headers: { 'Authorization': `Bearer ${jwt}` },
                                params: {
                                    id: { $in: groupIds },
                                    $limit: groupIds.length
                                }
                            });
                            const rawGroups = groupsResponse.data?.data || [];
                            groupsData = relData.map((rel) => {
                                const g = rawGroups.find((item) => parseInt(item.id, 10) === parseInt(rel.tbl_gruppoId, 10));
                                return {
                                    id: rel.id,
                                    tbl_gruppoId: rel.tbl_gruppoId,
                                    nome_gruppo: g?.nome || `Gruppo ${rel.tbl_gruppoId}`,
                                    read_permission: g?.read_permission ?? 1,
                                    create_permission: g?.create_permission ?? 1,
                                    update_permission: g?.update_permission ?? 1,
                                    remove_permission: g?.remove_permission ?? 1,
                                    flAttivo: rel.flAttivo ?? 1
                                };
                            });
                        } catch (fallbackError) {
                            console.warn("Errore fallback tbl-gruppo:", fallbackError);
                        }
                    }
                }

                groupsWithMain = (groupsData || []).map((g) => {
                    const rel = relData.find((r) =>
                        parseInt(r.id, 10) === parseInt(g.id, 10) ||
                        parseInt(r.tbl_gruppoId, 10) === parseInt(g.tbl_gruppoId, 10)
                    );
                    const isSingleGroup = (groupsData || []).length === 1;
                    return {
                        ...g,
                        flPrincipale: rel ? rel.flPrincipale : (isSingleGroup ? true : false)
                    };
                });

                if (groupsWithMain.length > 0 || attempt === 3) {
                    break;
                }
                await wait(450);
            }

            setGruppi(groupsWithMain);

            if (groupsWithMain.length > 0) {
                const groupIds = groupsWithMain.map(g => g.id);
                loadGroupsSettings(groupIds);
            }

        } catch (error) {
            console.error("Errore nel recupero gruppi:", error);
            setGruppi([]);
        } finally {
            setLoadingGruppi(false);
        }
    };

    // Helper per caricare i settings dei gruppi
    const loadGroupsSettings = async (groupIds) => {
        try {
            const jwt = getJwt();
            const response = await axios.get(`${host}/users-settings`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: {
                    tbl_users_gruppoId: { $in: groupIds },
                    $limit: 100
                }
            });

            const settingsData = response.data?.data || [];
            const settingsMap = {};
            settingsData.forEach(s => {
                settingsMap[s.tbl_users_gruppoId] = s;
            });

            setGroupSettings(settingsMap);

        } catch (error) {
            console.error("Errore nel recupero settings gruppi:", error);
        }
    };

    // Carica le transazioni Stripe dell'utente
    const loadUserTransactions = async (uid) => {
        try {
            setLoadingTransactions(true);
            const jwt = getJwt();

            const response = await axios.get(`${host}/tbl-transactions`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: {
                    usersId: uid,
                    $sort: { created_at: -1 },
                    $limit: 100
                }
            });

            setTransactions(response.data?.data || []);
        } catch (error) {
            console.error("Errore nel recupero transazioni:", error);
            setTransactions([]);
        } finally {
            setLoadingTransactions(false);
        }
    };

    // Carica abbonamento e storico
    const loadUserSubscription = async (uid) => {
        try {
            setLoadingSubscription(true);
            const jwt = getJwt();

            // 1. Get Active Subscription
            const subResponse = await axios.get(`${host}/tbl-users-piano`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: {
                    usersId: uid,
                    status: 'active',
                    $limit: 1
                }
            });

            const activeSub = subResponse.data?.data?.[0] || subResponse.data?.[0];

            if (activeSub) {
                // Fetch plan details
                try {
                    const planResponse = await axios.get(`${host}/tbl-piani/${activeSub.pianoId}`, {
                        headers: { 'Authorization': `Bearer ${jwt}` }
                    });
                    activeSub.piano = planResponse.data;
                } catch (e) {
                    console.error("Error fetching plan details", e);
                }
                setSubscription(activeSub);
            } else {
                setSubscription(null);
            }

            // 2. Get Subscription History
            // First get the subscription IDs for this user (active or not)
            const allSubsResponse = await axios.get(`${host}/tbl-users-piano`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: {
                    usersId: uid,
                    $select: ['id']
                }
            });

            const subIds = (allSubsResponse.data?.data || allSubsResponse.data || []).map(s => s.id);

            if (subIds.length > 0) {
                const historyResponse = await axios.get(`${host}/tbl-users-piano-transazione`, {
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: {
                        usersPianoId: { $in: subIds },
                        $sort: { createdAt: -1 }
                    }
                });
                setSubscriptionHistory(historyResponse.data?.data || []);
            } else {
                setSubscriptionHistory([]);
            }

        } catch (error) {
            console.error("Errore nel recupero abbonamento:", error);
        } finally {
            setLoadingSubscription(false);
        }
    };

    // Carica statistiche post
    const loadUserPostStats = async (uid) => {
        try {
            setLoadingPostStats(true);
            const jwt = getJwt();

            const response = await axios.get(`${host}/dynamic-proc`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: {
                    proc_name: 'proc_get_check_limiti_post',
                    in_usersId: uid,
                    in_tipo_chiamata: 1 // check iniziale
                }
            });

            // La proc restituisce i dati in response.data.data.tmp_check_limiti_post
            const stats = response.data?.data?.tmp_check_limiti_post?.[0];
            setPostStats(stats);
        } catch (error) {
            console.error("Errore nel recupero statistiche post:", error);
            setPostStats(null);
        } finally {
            setLoadingPostStats(false);
        }
    };

    // Carica i dati al mount e quando cambia userId
    React.useEffect(() => {
        if (userId) {
            loadUserDetails();
        }
    }, [userId]);

    // Calcola lo stato dell'utente
    const getUserStatus = () => {
        if (!user) return { label: 'N/A', color: '#9e9e9e', bgcolor: '#f5f5f5' };

        const isActive = user.status === 1 || user.status === true;
        if (isActive) {
            return { label: 'Attivo', color: '#4caf50', bgcolor: '#e8f5e8' };
        } else {
            return { label: 'Disattivo', color: '#f44336', bgcolor: '#ffebee' };
        }
    };

    const status = getUserStatus();

    // Loading skeleton
    if (loading) {
        return (
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
                    <Box sx={{ mb: 3 }}>
                        <Skeleton variant="text" width={300} height={24} />
                    </Box>
                    <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Skeleton variant="circular" width={80} height={80} />
                        <Box sx={{ flex: 1 }}>
                            <Skeleton variant="text" width={200} height={32} />
                            <Skeleton variant="text" width={150} height={20} />
                        </Box>
                    </Box>
                    <Grid container spacing={3}>
                        {[1, 2, 3, 4].map(i => (
                            <Grid item xs={12} md={6} key={i}>
                                <Card sx={{ p: 3 }}>
                                    <Skeleton variant="text" width={120} height={24} />
                                    <Skeleton variant="text" width="100%" height={20} sx={{ mt: 1 }} />
                                    <Skeleton variant="text" width="80%" height={20} />
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </motion.div>
            </Box>
        );
    }

    // Error state
    if (error) {
        return (
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
                    <Breadcrumbs sx={{ mb: 3 }}>
                        <MuiLink
                            component="button"
                            variant="body2"
                            onClick={() => navigate('/users')}
                            sx={{ textDecoration: 'none' }}
                        >
                            Utenti
                        </MuiLink>
                        <Typography variant="body2" color="text.primary">
                            Errore
                        </Typography>
                    </Breadcrumbs>

                    <Alert
                        severity="error"
                        sx={{ mb: 3 }}
                        action={
                            <Button color="inherit" size="small" onClick={loadUserDetails}>
                                Riprova
                            </Button>
                        }
                    >
                        {error}
                    </Alert>

                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/users')}
                    >
                        Torna alla lista
                    </Button>
                </motion.div>
            </Box>
        );
    }

    // Main content
    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
            >
                {/* Breadcrumb */}
                <Breadcrumbs sx={{ mb: 3 }}>
                    <MuiLink
                        component="button"
                        variant="body2"
                        onClick={() => navigate('/users')}
                        sx={{ textDecoration: 'none' }}
                    >
                        Utenti
                    </MuiLink>
                    <Typography variant="body2" color="text.primary">
                        {user?.email || `Utente ${userId}`}
                    </Typography>
                </Breadcrumbs>

                {/* Header con info principale */}
                <Card sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>

                        {/* Avatar */}
                        <Avatar
                            sx={{
                                width: 80,
                                height: 80,
                                bgcolor: ROLE_COLORS[user?.roles] || '#757575',
                                fontSize: '2rem',
                                fontWeight: 'bold'
                            }}
                        >
                            {getInitials(user?.nome, user?.cognome)}
                        </Avatar>

                        {/* Info principale */}
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                                {user?.nome && user?.cognome ? `${user.nome} ${user.cognome}` : user?.email || 'Nome non disponibile'}
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                                <EmailIcon sx={{ color: '#999', fontSize: 18 }} />
                                <Typography variant="body1" color="text.secondary">
                                    {user?.email}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                                <Chip
                                    label={status.label}
                                    icon={status.label === 'Attivo' ? <CheckCircleIcon /> : <CancelIcon />}
                                    sx={{
                                        color: status.color,
                                        bgcolor: status.bgcolor,
                                        fontWeight: 500
                                    }}
                                />
                                <Chip
                                    icon={<PersonIcon sx={{ fontSize: 16 }} />}
                                    label={ROLE_LABELS[user?.roles] || user?.roles}
                                    sx={{
                                        bgcolor: ROLE_COLORS[user?.roles] || '#757575',
                                        color: '#fff',
                                        fontWeight: 500,
                                        '& .MuiChip-icon': { color: '#fff' }
                                    }}
                                />
                            </Box>

                            <Typography variant="body2" color="text.secondary">
                                ID Utente: {user?.id}
                            </Typography>
                        </Box>

                        {/* Azioni */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate('/users')}
                                size="small"
                            >
                                Indietro
                            </Button>

                            <Button
                                variant="contained"
                                startIcon={<EditIcon />}
                                onClick={() => navigate(`/users/${userId}/edit`)}
                                size="small"
                            >
                                Modifica
                            </Button>

                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={loadUserDetails}
                                size="small"
                            >
                                Aggiorna
                            </Button>
                        </Box>
                    </Box>
                </Card>

                {/* Banner richiesta cancellazione */}
                {deletionRequest && (
                    <Alert
                        severity="error"
                        icon={<DeleteForeverIcon />}
                        sx={{ mb: 3, border: '1px solid rgba(211,47,47,0.4)', fontWeight: 500 }}
                        action={
                            <Button
                                color="error"
                                size="small"
                                variant="outlined"
                                onClick={() => navigate('/richieste-cancellazione')}
                            >
                                Gestisci
                            </Button>
                        }
                    >
                        <strong>Richiesta di cancellazione account in attesa.</strong>
                        {' '}L'utente ha richiesto la cancellazione del proprio account il{' '}
                        <strong>{new Date(deletionRequest.createdAt).toLocaleDateString('it-IT')}</strong>.
                        {deletionRequest.motivo && <> Motivo: <em>{deletionRequest.motivo}</em></>}
                    </Alert>
                )}

                {/* Tabs Navigation */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, newValue) => {
                            setActiveTab(newValue);
                            if (newValue === 2 && transactions.length === 0) {
                                loadUserTransactions(userId);
                            }
                            if (newValue === 3 && !subscription && !loadingSubscription) {
                                loadUserSubscription(userId);
                            }
                            if (newValue === 3 && !postStats && !loadingPostStats) {
                                loadUserPostStats(userId);
                            }
                        }}
                        aria-label="user detail tabs"
                    >
                        <Tab label="📋 Informazioni" />
                        <Tab label="👥 Gruppi" />
                        <Tab label="💳 Transazioni Stripe" />
                        <Tab label="⭐ Abbonamento" />
                    </Tabs>
                </Box>

                {/* Tab 0: Informazioni */}
                {activeTab === 0 && (
                    <Grid container spacing={3}>
                        {/* Informazioni Utente */}
                        <Grid item xs={12} md={4}>
                            <Card sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <InfoIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Informazioni Utente
                                    </Typography>
                                </Box>

                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 500, width: '40%' }}>Nome</TableCell>
                                            <TableCell>{user?.nome || 'N/A'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 500 }}>Cognome</TableCell>
                                            <TableCell>{user?.cognome || 'N/A'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 500 }}>Email</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <EmailIcon sx={{ fontSize: 16, color: '#999' }} />
                                                    {user?.email}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 500 }}>ID Utente</TableCell>
                                            <TableCell>{user?.id}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </Card>
                        </Grid>

                        {/* Ruolo e Permessi */}
                        <Grid item xs={12} md={4}>
                            <Card sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <PersonIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Ruolo e Permessi
                                    </Typography>
                                </Box>

                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 500, width: '40%' }}>Ruolo</TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={<PersonIcon />}
                                                    label={ROLE_LABELS[user?.roles] || user?.roles}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: ROLE_COLORS[user?.roles] || '#757575',
                                                        color: '#fff',
                                                        fontWeight: 'bold',
                                                        '& .MuiChip-icon': { color: '#fff' }
                                                    }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 500 }}>Stato</TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={status.label === 'Attivo' ? <CheckCircleIcon /> : <CancelIcon />}
                                                    label={status.label}
                                                    size="small"
                                                    color={status.label === 'Attivo' ? 'success' : 'error'}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </Card>
                        </Grid>

                        {/* Squadra & Gruppo Principale */}
                        {(() => {
                            const mainGroup = gruppi.find(g => g.flPrincipale) || gruppi[0];
                            const mainSettings = mainGroup ? groupSettings[mainGroup.id] : null;
                            return (
                                <Grid item xs={12} md={4}>
                                    <Card sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <SportsSoccerIcon color="primary" />
                                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                Squadra & Gruppo
                                            </Typography>
                                        </Box>
                                        <Table size="small">
                                            <TableBody>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 500, width: '40%' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <GroupsIcon sx={{ fontSize: 16, color: '#999' }} />
                                                            Gruppo
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        {mainGroup ? (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <Chip
                                                                    label={mainGroup.nome_gruppo || `Gruppo ${mainGroup.tbl_gruppoId}`}
                                                                    size="small"
                                                                    icon={<StarIcon sx={{ fontSize: '14px !important' }} />}
                                                                    sx={{ bgcolor: '#1976d2', color: '#fff', fontWeight: 600, '& .MuiChip-icon': { color: '#fff' } }}
                                                                />
                                                            </Box>
                                                        ) : (
                                                            <Typography variant="body2" color="text.secondary">N/A</Typography>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 500 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <SportsSoccerIcon sx={{ fontSize: 16, color: '#999' }} />
                                                            Squadra
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        {mainSettings?.selectedTeamName ? (
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {mainSettings.selectedTeamName}
                                                            </Typography>
                                                        ) : (
                                                            <Typography variant="body2" color="text.secondary">Non selezionata</Typography>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 500 }}>Lega</TableCell>
                                                    <TableCell>
                                                        {mainSettings?.selectedLegaName ? (
                                                            <Typography variant="body2">{mainSettings.selectedLegaName}</Typography>
                                                        ) : (
                                                            <Typography variant="body2" color="text.secondary">Non selezionata</Typography>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                        <Box sx={{
                                            mt: 2,
                                            p: 1.5,
                                            borderRadius: 1.5,
                                            bgcolor: 'action.hover',
                                            border: '1px dashed',
                                            borderColor: 'divider',
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 1
                                        }}>
                                            <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', mt: 0.1, flexShrink: 0 }} />
                                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                                                Per modificare squadra e lega vai nel tab <strong>Gruppi</strong>, apri la sezione <strong>Gruppi Assegnati</strong> e clicca l'icona <strong>✏️ (pennino)</strong> accanto al gruppo. La <strong>⭐</strong> indica solo quale gruppo è già impostato come principale.
                                            </Typography>
                                        </Box>
                                    </Card>
                                </Grid>
                            );
                        })()}

                        {/* Timestamp */}
                        <Grid item xs={12}>
                            <Card sx={{ p: 3, borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <CalendarTodayIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Timestamp
                                    </Typography>
                                </Box>

                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <Table size="small">
                                            <TableBody>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 500, width: '40%' }}>Creato il</TableCell>
                                                    <TableCell>
                                                        {user?.createdAt ?
                                                            new Date(user.createdAt).toLocaleString('it-IT') :
                                                            'N/A'
                                                        }
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Table size="small">
                                            <TableBody>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 500, width: '40%' }}>Aggiornato il</TableCell>
                                                    <TableCell>
                                                        {user?.updatedAt ?
                                                            new Date(user.updatedAt).toLocaleString('it-IT') :
                                                            'N/A'
                                                        }
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </Grid>
                                </Grid>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* Tab 1: Gruppi */}
                {activeTab === 1 && (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Card sx={{ p: 3, borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <GroupIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Gruppi Assegnati
                                    </Typography>
                                </Box>

                                {loadingGruppi ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : gruppi.length > 0 ? (
                                    <Box>
                                        {gruppi.map((gruppo, index) => {
                                            const settings = groupSettings[gruppo.id];

                                            return (
                                                <Accordion key={index} sx={{ mb: 2, '&:before': { display: 'none' }, boxShadow: 1 }}>
                                                    <AccordionSummary
                                                        expandIcon={<ExpandMoreIcon />}
                                                        sx={{
                                                            bgcolor: 'rgba(0, 0, 0, 0.02)',
                                                            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2, flexWrap: 'wrap' }}>
                                                            {/* Gruppo Info */}
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200 }}>
                                                                <GroupIcon color="primary" />
                                                                <Typography variant="subtitle1" fontWeight="bold">
                                                                    {gruppo.nome_gruppo}
                                                                </Typography>
                                                                {!!gruppo.flPrincipale && (
                                                                    <Tooltip title="Gruppo Principale">
                                                                        <StarIcon sx={{ color: '#FFD700' }} />
                                                                    </Tooltip>
                                                                )}
                                                            </Box>

                                                            {/* Permessi Badges */}
                                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                                <Tooltip title="Lettura">
                                                                    <Chip
                                                                        label="R"
                                                                        size="small"
                                                                        color={gruppo.read_permission ? "success" : "default"}
                                                                        variant={gruppo.read_permission ? "filled" : "outlined"}
                                                                        sx={{ width: 24, height: 24, '& .MuiChip-label': { px: 0 } }}
                                                                    />
                                                                </Tooltip>
                                                                <Tooltip title="Creazione">
                                                                    <Chip
                                                                        label="C"
                                                                        size="small"
                                                                        color={gruppo.create_permission ? "success" : "default"}
                                                                        variant={gruppo.create_permission ? "filled" : "outlined"}
                                                                        sx={{ width: 24, height: 24, '& .MuiChip-label': { px: 0 } }}
                                                                    />
                                                                </Tooltip>
                                                                <Tooltip title="Modifica">
                                                                    <Chip
                                                                        label="U"
                                                                        size="small"
                                                                        color={gruppo.update_permission ? "success" : "default"}
                                                                        variant={gruppo.update_permission ? "filled" : "outlined"}
                                                                        sx={{ width: 24, height: 24, '& .MuiChip-label': { px: 0 } }}
                                                                    />
                                                                </Tooltip>
                                                                <Tooltip title="Eliminazione">
                                                                    <Chip
                                                                        label="D"
                                                                        size="small"
                                                                        color={gruppo.remove_permission ? "success" : "default"}
                                                                        variant={gruppo.remove_permission ? "filled" : "outlined"}
                                                                        sx={{ width: 24, height: 24, '& .MuiChip-label': { px: 0 } }}
                                                                    />
                                                                </Tooltip>
                                                            </Box>

                                                            <Box sx={{ flex: 1 }} />

                                                            <Chip
                                                                label={gruppo.flAttivo ? 'Attivo' : 'Inattivo'}
                                                                size="small"
                                                                color={gruppo.flAttivo ? 'success' : 'default'}
                                                            />
                                                        </Box>
                                                    </AccordionSummary>
                                                    <AccordionDetails sx={{ bgcolor: '#fafafa', p: 3 }}>
                                                        {settings ? (
                                                            <Grid container spacing={3}>
                                                                {/* Intestazione Settings */}
                                                                <Grid item xs={12}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                                        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                                                                            ⚙️ Impostazioni Utente per questo Gruppo lato APP
                                                                        </Typography>
                                                                        {settings.isCompleted ? (
                                                                            <Chip label="Profilo Completato" size="small" color="success" icon={<CheckCircleIcon />} />
                                                                        ) : (
                                                                            <Chip label="Incompleto" size="small" color="warning" />
                                                                        )}
                                                                        <Box sx={{ flex: 1 }} />
                                                                        <Tooltip title="Modifica Impostazioni">
                                                                            <IconButton size="small" onClick={(e) => handleOpenSettings(e, settings)} sx={{ mr: 1 }}>
                                                                                <EditIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        <Tooltip title="Vedi JSON Raw">
                                                                            <IconButton size="small" onClick={() => handleOpenDebug(settings)}>
                                                                                <InfoIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </Box>
                                                                    {!settings.isCompleted && (
                                                                        <Alert severity="warning" sx={{ mt: 1, mb: 1, borderRadius: 2 }}>
                                                                            <strong>Attenzione:</strong> Avendo valore <strong>0</strong> in app si avvia il primo accesso con il flusso della scelta lega squadra non più necessario.
                                                                        </Alert>
                                                                    )}
                                                                </Grid>

                                                                {/* Colonna 1: Profilo e Contatti */}
                                                                <Grid item xs={12} md={6}>
                                                                    <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
                                                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 2 }}>
                                                                            PROFILO & CONTATTI
                                                                        </Typography>
                                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                                            <Box>
                                                                                <Typography variant="caption" color="text.secondary">Nome Società / Visualizzato</Typography>
                                                                                <Typography variant="body2" fontWeight="500">{settings.nomeSocieta || '-'}</Typography>
                                                                            </Box>
                                                                            <Box>
                                                                                <Typography variant="caption" color="text.secondary">Tipo Account</Typography>
                                                                                <Typography variant="body2">{settings.accountType}</Typography>
                                                                            </Box>
                                                                            <Box>
                                                                                <Typography variant="caption" color="text.secondary">Email</Typography>
                                                                                <Typography variant="body2">{settings.email || '-'}</Typography>
                                                                            </Box>
                                                                            <Box>
                                                                                <Typography variant="caption" color="text.secondary">Telefono</Typography>
                                                                                <Typography variant="body2">{settings.telefono || '-'}</Typography>
                                                                            </Box>
                                                                            <Box>
                                                                                <Typography variant="caption" color="text.secondary">Indirizzo</Typography>
                                                                                <Typography variant="body2">
                                                                                    {[settings.indirizzo, settings.citta, settings.provincia, settings.cap].filter(Boolean).join(', ') || '-'}
                                                                                </Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    </Card>
                                                                </Grid>

                                                                {/* Colonna 2: Preferenze e Social */}
                                                                <Grid item xs={12} md={6}>
                                                                    <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
                                                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 2 }}>
                                                                            PREFERENZE & SOCIAL
                                                                        </Typography>

                                                                        <Grid container spacing={2}>
                                                                            <Grid item xs={12} sm={6}>
                                                                                <Box sx={{ mb: 2 }}>
                                                                                    <Typography variant="caption" color="text.secondary">Colori</Typography>
                                                                                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                                                                        <Tooltip title={`Primary: ${settings.primaryColor}`}><Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: settings.primaryColor, border: '1px solid #ddd' }} /></Tooltip>
                                                                                        <Tooltip title={`Secondary: ${settings.secondaryColor}`}><Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: settings.secondaryColor, border: '1px solid #ddd' }} /></Tooltip>
                                                                                        <Tooltip title={`Accent: ${settings.accentColor}`}><Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: settings.accentColor, border: '1px solid #ddd' }} /></Tooltip>
                                                                                    </Box>
                                                                                </Box>
                                                                            </Grid>
                                                                            <Grid item xs={12} sm={6}>
                                                                                <Box sx={{ mb: 2 }}>
                                                                                    <Typography variant="caption" color="text.secondary">Preferiti</Typography>
                                                                                    {settings.selectedTeamName && <Chip size="small" label={`Team: ${settings.selectedTeamName}`} sx={{ mt: 0.5, mr: 1 }} />}
                                                                                    {settings.selectedLegaName && <Chip size="small" label={`Lega: ${settings.selectedLegaName}`} sx={{ mt: 0.5 }} />}
                                                                                    {settings.has_no_favorite_team ? <Typography variant="caption" display="block">Nessun team preferito</Typography> : null}
                                                                                </Box>
                                                                            </Grid>
                                                                        </Grid>

                                                                        <Box sx={{ mt: 1 }}>
                                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Links</Typography>
                                                                            {settings.sitoWeb && <MuiLink href={settings.sitoWeb} target="_blank" sx={{ mr: 2, fontSize: '0.875rem' }}>Sito Web</MuiLink>}
                                                                            {settings.facebookUrl && <MuiLink href={settings.facebookUrl} target="_blank" sx={{ mr: 2, fontSize: '0.875rem' }}>Facebook</MuiLink>}
                                                                            {settings.instagramUrl && <MuiLink href={settings.instagramUrl} target="_blank" sx={{ mr: 2, fontSize: '0.875rem' }}>Instagram</MuiLink>}
                                                                        </Box>
                                                                    </Card>
                                                                </Grid>

                                                                {/* Colonna 3: Notifiche e Altro */}
                                                                <Grid item xs={12}>
                                                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                                                        <Chip label="Risultati" color={settings.notifiche_risultati ? "success" : "default"} variant={settings.notifiche_risultati ? "filled" : "outlined"} size="small" icon={settings.notifiche_risultati ? <CheckCircleIcon /> : <CancelIcon />} />
                                                                        <Chip label="News" color={settings.notifiche_news ? "success" : "default"} variant={settings.notifiche_news ? "filled" : "outlined"} size="small" icon={settings.notifiche_news ? <CheckCircleIcon /> : <CancelIcon />} />
                                                                        <Chip label="Live" color={settings.notifiche_live ? "success" : "default"} variant={settings.notifiche_live ? "filled" : "outlined"} size="small" icon={settings.notifiche_live ? <CheckCircleIcon /> : <CancelIcon />} />
                                                                        <Chip label="Profilo Pubblico" color={settings.profilo_pubblico ? "primary" : "default"} variant={settings.profilo_pubblico ? "filled" : "outlined"} size="small" />
                                                                        <Chip label="Statistiche Condivise" color={settings.condiviso_statistiche ? "primary" : "default"} variant={settings.condiviso_statistiche ? "filled" : "outlined"} size="small" />
                                                                    </Box>
                                                                </Grid>
                                                            </Grid>
                                                        ) : (
                                                            <Alert severity="info" variant="outlined">
                                                                Nessuna impostazione trovata per questo gruppo.
                                                            </Alert>
                                                        )}
                                                    </AccordionDetails>
                                                </Accordion>
                                            );
                                        })}
                                    </Box>
                                ) : (
                                    <Alert severity="warning">
                                        ⚠️ Nessun gruppo assegnato a questo utente
                                    </Alert>
                                )}
                            </Card>
                        </Grid>

                        {/* Admin message */}
                        {user?.roles === 'admin' && (
                            <Grid item xs={12}>
                                <Alert severity="info">
                                    Gli amministratori hanno accesso completo al sistema e non necessitano di gruppi specifici.
                                </Alert>
                            </Grid>
                        )}
                    </Grid>
                )}

                {/* Tab 2: Transazioni Stripe */}
                {activeTab === 2 && (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Card sx={{ p: 3, borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <PaymentIcon color="primary" />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Transazioni Stripe
                                        </Typography>
                                    </Box>
                                    <Button
                                        variant="outlined"
                                        startIcon={<RefreshIcon />}
                                        onClick={() => loadUserTransactions(userId)}
                                        size="small"
                                    >
                                        Aggiorna
                                    </Button>
                                </Box>

                                {loadingTransactions ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : transactions.length > 0 ? (
                                    <Box>
                                        {transactions.map((transaction, index) => {
                                            const status = transaction.status || 'pending';
                                            const StatusIcon = STRIPE_STATUS_ICONS[status];
                                            const planInfo = transaction.metadata?.planInfo || {};

                                            return (
                                                <Accordion key={transaction.id} sx={{ mb: 2 }}>
                                                    <AccordionSummary
                                                        expandIcon={<ExpandMoreIcon />}
                                                        sx={{
                                                            bgcolor: 'rgba(0, 0, 0, 0.02)',
                                                            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                                                            <Chip
                                                                icon={<StatusIcon />}
                                                                label={STRIPE_STATUS_LABELS[status]}
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: STRIPE_STATUS_COLORS[status],
                                                                    color: '#fff',
                                                                    fontWeight: 'bold',
                                                                    '& .MuiChip-icon': { color: '#fff' }
                                                                }}
                                                            />
                                                            <Typography variant="body1" fontWeight="bold">
                                                                {(transaction.amount / 100).toFixed(2)} {(transaction.currency || 'EUR').toUpperCase()}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                                                                {transaction.description || 'N/A'}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {transaction.created_at ?
                                                                    new Date(transaction.created_at).toLocaleDateString('it-IT') :
                                                                    'N/A'
                                                                }
                                                            </Typography>
                                                        </Box>
                                                    </AccordionSummary>
                                                    <AccordionDetails>
                                                        <Grid container spacing={2}>
                                                            {/* Dettagli Transazione */}
                                                            <Grid item xs={12}>
                                                                <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
                                                                    📋 Dettagli Transazione
                                                                </Typography>
                                                            </Grid>

                                                            <Grid item xs={12} md={6}>
                                                                <Typography variant="caption" color="text.secondary">ID Transazione</Typography>
                                                                <Typography variant="body2" fontWeight="500">#{transaction.id}</Typography>
                                                            </Grid>

                                                            <Grid item xs={12} md={6}>
                                                                <Typography variant="caption" color="text.secondary">Importo</Typography>
                                                                <Typography variant="body2" fontWeight="bold" color="primary">
                                                                    {(transaction.amount / 100).toFixed(2)} {(transaction.currency || 'EUR').toUpperCase()}
                                                                </Typography>
                                                            </Grid>

                                                            <Grid item xs={12} md={6}>
                                                                <Typography variant="caption" color="text.secondary">Data Creazione</Typography>
                                                                <Typography variant="body2">
                                                                    {transaction.created_at ?
                                                                        new Date(transaction.created_at).toLocaleString('it-IT') :
                                                                        'N/A'
                                                                    }
                                                                </Typography>
                                                            </Grid>

                                                            <Grid item xs={12} md={6}>
                                                                <Typography variant="caption" color="text.secondary">Ultimo Aggiornamento</Typography>
                                                                <Typography variant="body2">
                                                                    {transaction.updated_at ?
                                                                        new Date(transaction.updated_at).toLocaleString('it-IT') :
                                                                        'N/A'
                                                                    }
                                                                </Typography>
                                                            </Grid>

                                                            <Grid item xs={12}>
                                                                <Typography variant="caption" color="text.secondary">Descrizione</Typography>
                                                                <Typography variant="body2">{transaction.description || 'Nessuna descrizione'}</Typography>
                                                            </Grid>

                                                            <Grid item xs={12}>
                                                                <Typography variant="caption" color="text.secondary">Payment Intent ID</Typography>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            fontFamily: 'monospace',
                                                                            fontSize: '0.875rem',
                                                                            bgcolor: 'rgba(0, 0, 0, 0.05)',
                                                                            p: 0.5,
                                                                            borderRadius: 1,
                                                                            flex: 1
                                                                        }}
                                                                    >
                                                                        {transaction.stripe_payment_intent_id || 'N/A'}
                                                                    </Typography>
                                                                    {transaction.stripe_payment_intent_id && (
                                                                        <Tooltip title="Copia Payment Intent ID">
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => copyToClipboard(transaction.stripe_payment_intent_id)}
                                                                                sx={{ color: 'primary.main' }}
                                                                            >
                                                                                <ContentCopyIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    )}
                                                                </Box>
                                                            </Grid>

                                                            {/* Dettagli Piano */}
                                                            {planInfo && Object.keys(planInfo).length > 0 && (
                                                                <>
                                                                    <Grid item xs={12} sx={{ mt: 2 }}>
                                                                        <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
                                                                            💎 Dettagli Piano
                                                                        </Typography>
                                                                    </Grid>

                                                                    {planInfo.nome && (
                                                                        <Grid item xs={12} md={6}>
                                                                            <Typography variant="caption" color="text.secondary">Nome Piano</Typography>
                                                                            <Typography variant="body2" fontWeight="600">{planInfo.nome}</Typography>
                                                                        </Grid>
                                                                    )}

                                                                    {planInfo.prezzo && (
                                                                        <Grid item xs={12} md={6}>
                                                                            <Typography variant="caption" color="text.secondary">Prezzo Piano</Typography>
                                                                            <Typography variant="body2">{planInfo.prezzo} €</Typography>
                                                                        </Grid>
                                                                    )}

                                                                    {planInfo.maxPostMonth && (
                                                                        <Grid item xs={12} md={6}>
                                                                            <Typography variant="caption" color="text.secondary">Max Post/Mese</Typography>
                                                                            <Typography variant="body2">{planInfo.maxPostMonth}</Typography>
                                                                        </Grid>
                                                                    )}

                                                                    {planInfo.watermark !== undefined && (
                                                                        <Grid item xs={12} md={6}>
                                                                            <Typography variant="caption" color="text.secondary">Watermark</Typography>
                                                                            <Chip
                                                                                label={planInfo.watermark ? 'Sì' : 'No'}
                                                                                size="small"
                                                                                color={planInfo.watermark ? 'default' : 'success'}
                                                                            />
                                                                        </Grid>
                                                                    )}
                                                                </>
                                                            )}
                                                        </Grid>
                                                    </AccordionDetails>
                                                </Accordion>
                                            );
                                        })}
                                    </Box>
                                ) : (
                                    <Alert severity="info">
                                        📭 Nessuna transazione trovata per questo utente
                                    </Alert>
                                )}
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* Tab 3: Abbonamento */}
                {activeTab === 3 && (
                    <Grid container spacing={3}>
                        {/* Active Subscription */}
                        <Grid item xs={12} md={6}>
                            <Card sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <CheckCircleIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Abbonamento Attivo
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                    {subscription ? (
                                        <>
                                            <Button size="small" variant="contained" onClick={openEditSubscriptionDialog}>
                                                Modifica abbonamento
                                            </Button>
                                            <Button size="small" color="error" variant="outlined" onClick={() => setCancelDialogOpen(true)}>
                                                Cancella abbonamento
                                            </Button>
                                        </>
                                    ) : (
                                        <Button size="small" variant="contained" onClick={openCreateSubscriptionDialog}>
                                            Assegna abbonamento
                                        </Button>
                                    )}
                                </Box>

                                {loadingSubscription ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : subscription ? (
                                    <Table size="small">
                                        <TableBody>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500 }}>Piano</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={subscription.piano?.nome || 'Sconosciuto'}
                                                        color="primary"
                                                        size="small"
                                                        sx={{ fontWeight: 'bold' }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500 }}>Stato</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={subscription.status}
                                                        color={subscription.status === 'active' ? 'success' : 'default'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500 }}>Durata</TableCell>
                                                <TableCell sx={{ textTransform: 'capitalize' }}>{subscription.durataType}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500 }}>Inizio</TableCell>
                                                <TableCell>{new Date(subscription.dataInizio).toLocaleDateString('it-IT')}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500 }}>Scadenza</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', color: 'error.main' }}>
                                                    {new Date(subscription.dataScadenza).toLocaleDateString('it-IT')}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 500 }}>Rinnovo Auto</TableCell>
                                                <TableCell>{subscription.autoRinnovo ? 'Sì' : 'No'}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <Alert severity="info">Nessun abbonamento attivo trovato.</Alert>
                                )}
                            </Card>
                        </Grid>

                        {/* Subscription History */}
                        <Grid item xs={12} md={6}>
                            <Card sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <CalendarTodayIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Storico Eventi
                                    </Typography>
                                </Box>

                                {loadingSubscription ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : subscriptionHistory.length > 0 ? (
                                    <TableContainer sx={{ maxHeight: 300 }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Data</TableCell>
                                                    <TableCell>Tipo</TableCell>
                                                    <TableCell>Periodo</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {subscriptionHistory.map((event) => (
                                                    <TableRow key={event.id}>
                                                        <TableCell>{new Date(event.createdAt || event.periodoInizio).toLocaleDateString('it-IT')}</TableCell>
                                                        <TableCell sx={{ textTransform: 'capitalize' }}>{event.tipo}</TableCell>
                                                        <TableCell>
                                                            {new Date(event.periodoInizio).toLocaleDateString('it-IT')} - {new Date(event.periodoFine).toLocaleDateString('it-IT')}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                ) : (
                                    <Alert severity="info">Nessun evento storico trovato.</Alert>
                                )}
                            </Card>
                        </Grid>

                        {/* Statistiche Post */}
                        <Grid item xs={12}>
                            <Card sx={{ p: 3, borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <InfoIcon color="primary" />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Statistiche Post
                                        </Typography>
                                    </Box>
                                    <Button
                                        variant="outlined"
                                        startIcon={<RefreshIcon />}
                                        onClick={() => loadUserPostStats(userId)}
                                        size="small"
                                    >
                                        Aggiorna
                                    </Button>
                                </Box>

                                {loadingPostStats ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : postStats ? (
                                    <Box>
                                        {/* Riepilogo Esito */}
                                        <Alert
                                            severity={postStats.checkEsito === 'OK' ? "success" : "warning"}
                                            sx={{ mb: 3 }}
                                        >
                                            <strong>{postStats.checkEsitoDescrizione}</strong>
                                            {postStats.pianoDescrizione && (
                                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                    Piano di riferimento: <strong>{postStats.pianoDescrizione}</strong>
                                                    {postStats.gruppoDescrizione ? ` (${postStats.gruppoDescrizione})` : ''}
                                                </Typography>
                                            )}
                                        </Alert>

                                        <Grid container spacing={3}>
                                            {/* Daily Stats */}
                                            <Grid item xs={12} md={4}>
                                                <Card variant="outlined" sx={{ p: 2, textAlign: 'center', borderColor: postStats.nPostGgCheck === 'OK' ? 'success.light' : 'warning.light' }}>
                                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>GIORNALIERO</Typography>
                                                    <Typography variant="h4" sx={{ fontWeight: 700, color: postStats.nPostGgCheck === 'OK' ? 'success.main' : 'warning.main' }}>
                                                        {postStats.nPostGgDone} / {postStats.maxPostGg}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Rimasti: <strong>{postStats.nPostGgToDo}</strong>
                                                    </Typography>
                                                    <Box sx={{ mt: 1 }}>
                                                        <Chip
                                                            label={postStats.nPostGgCheck}
                                                            size="small"
                                                            color={postStats.nPostGgCheck === 'OK' ? 'success' : 'warning'}
                                                        />
                                                    </Box>
                                                </Card>
                                            </Grid>

                                            {/* Weekly Stats */}
                                            <Grid item xs={12} md={4}>
                                                <Card variant="outlined" sx={{ p: 2, textAlign: 'center', borderColor: postStats.nPostWeekCheck === 'OK' ? 'success.light' : 'warning.light' }}>
                                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>SETTIMANALE</Typography>
                                                    <Typography variant="h4" sx={{ fontWeight: 700, color: postStats.nPostWeekCheck === 'OK' ? 'success.main' : 'warning.main' }}>
                                                        {postStats.nPostWeekDone} / {postStats.maxPostWeek}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Rimasti: <strong>{postStats.nPostWeekToDo}</strong>
                                                    </Typography>
                                                    <Box sx={{ mt: 1 }}>
                                                        <Chip
                                                            label={postStats.nPostWeekCheck}
                                                            size="small"
                                                            color={postStats.nPostWeekCheck === 'OK' ? 'success' : 'warning'}
                                                        />
                                                    </Box>
                                                </Card>
                                            </Grid>

                                            {/* Monthly Stats */}
                                            <Grid item xs={12} md={4}>
                                                <Card variant="outlined" sx={{ p: 2, textAlign: 'center', borderColor: postStats.nPostMonthCheck === 'OK' ? 'success.light' : 'warning.light' }}>
                                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>MENSILE</Typography>
                                                    <Typography variant="h4" sx={{ fontWeight: 700, color: postStats.nPostMonthCheck === 'OK' ? 'success.main' : 'warning.main' }}>
                                                        {postStats.nPostMonthDone} / {postStats.maxPostMonth}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Rimasti: <strong>{postStats.nPostMonthToDo}</strong>
                                                    </Typography>
                                                    <Box sx={{ mt: 1 }}>
                                                        <Chip
                                                            label={postStats.nPostMonthCheck}
                                                            size="small"
                                                            color={postStats.nPostMonthCheck === 'OK' ? 'success' : 'warning'}
                                                        />
                                                    </Box>
                                                </Card>
                                            </Grid>
                                        </Grid>

                                        {/* Debug info if needed */}
                                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                            <Tooltip title="Vedi Dati Raw">
                                                <IconButton size="small" onClick={() => handleOpenDebug(postStats)}>
                                                    <InfoIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Alert severity="info">Nessuna statistica post disponibile.</Alert>
                                )}
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* Footer con ultimo aggiornamento */}
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Ultimo aggiornamento: {new Date().toLocaleString('it-IT')}
                    </Typography>
                </Box>

            </motion.div>

            {/* Debug Dialog */}
            <Dialog open={debugDialog.open} onClose={handleCloseDebug} maxWidth="md" fullWidth>
                <DialogTitle>
                    Debug: Raw JSON Settings
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseDebug}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CancelIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Box
                        component="pre"
                        sx={{
                            bgcolor: '#f5f5f5',
                            p: 2,
                            borderRadius: 1,
                            overflow: 'auto',
                            maxHeight: 500,
                            fontSize: '0.8rem'
                        }}
                    >
                        {JSON.stringify(debugDialog.data, null, 2)}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => copyToClipboard(JSON.stringify(debugDialog.data, null, 2))} startIcon={<ContentCopyIcon />}>
                        Copia JSON
                    </Button>
                    <Button onClick={handleCloseDebug}>Chiudi</Button>
                </DialogActions>
            </Dialog>

            {/* Edit Settings Dialog */}
            <UsersGroupSettingsDialog
                open={openSettingsDialog}
                onClose={handleCloseSettings}
                settings={selectedSettings}
                onSave={handleSaveSettings}
            />

            {/* Subscription Create/Edit Dialog */}
            <Dialog
                open={subscriptionDialog.open}
                onClose={closeSubscriptionDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {subscriptionDialog.mode === 'create' ? 'Assegna abbonamento' : 'Modifica abbonamento'}
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <TextField
                            select
                            label="Piano"
                            value={subscriptionForm.pianoId}
                            onChange={(e) => handleSubscriptionFormChange('pianoId', e.target.value)}
                            fullWidth
                            disabled={loadingAvailablePlans || subscriptionDialog.loading}
                        >
                            {filteredPlansForSubscription.map((plan) => (
                                <MenuItem key={plan.id} value={plan.id}>
                                    {plan.nome}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            select
                            label="Durata"
                            value={subscriptionForm.durataType}
                            onChange={(e) => handleSubscriptionFormChange('durataType', e.target.value)}
                            fullWidth
                            disabled={subscriptionDialog.loading}
                        >
                            <MenuItem value="mensile">Mensile</MenuItem>
                        <MenuItem value="annuale">Annuale</MenuItem>
                        </TextField>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!subscriptionForm.cancel_at_period_end}
                                    onChange={(e) => handleSubscriptionFormChange('cancel_at_period_end', !e.target.checked)}
                                    color="primary"
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body2">Rinnovo Automatico</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Se attivo, l'abbonamento si rinnoverà automaticamente alla scadenza.
                                    </Typography>
                                </Box>
                            }
                            sx={{ mt: 1, mb: 1 }}
                        />

                        {subscriptionDialog.mode === 'create' && (
                            <Box sx={{ p: 1.5, border: '1px dashed #d0d7de', borderRadius: 1.5 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Prova gratuita rapida
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <Button size="small" variant="outlined" onClick={() => applyTrialPreset(7)} disabled={subscriptionDialog.loading}>
                                        7 giorni
                                    </Button>
                                    <Button size="small" variant="outlined" onClick={() => applyTrialPreset(14)} disabled={subscriptionDialog.loading}>
                                        14 giorni
                                    </Button>
                                    <Button size="small" variant="outlined" onClick={() => applyTrialPreset(30)} disabled={subscriptionDialog.loading}>
                                        30 giorni
                                    </Button>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    Imposta automaticamente data inizio e data scadenza per il periodo di prova.
                                </Typography>
                            </Box>
                        )}

                        <TextField
                            label="Data inizio"
                            type={subscriptionDialog.mode === 'create' ? 'datetime-local' : 'date'}
                            value={subscriptionForm.dataInizio}
                            onChange={(e) => handleSubscriptionFormChange('dataInizio', e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            disabled={subscriptionDialog.loading}
                        />

                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                Durata periodo
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {DURATA_PRESETS.map(({ label, months }) => (
                                    <Button
                                        key={months}
                                        size="small"
                                        variant={durataPreset === months ? 'contained' : 'outlined'}
                                        onClick={() => applyDurataPreset(months)}
                                        disabled={subscriptionDialog.loading}
                                    >
                                        {label}
                                    </Button>
                                ))}
                            </Box>
                            {subscriptionForm.dataScadenza && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Scadenza: <strong>{new Date(subscriptionForm.dataScadenza).toLocaleDateString('it-IT')}</strong>
                                </Typography>
                            )}
                        </Box>

                        <TextField
                            label="Note"
                            value={subscriptionForm.note}
                            onChange={(e) => handleSubscriptionFormChange('note', e.target.value)}
                            multiline
                            minRows={3}
                            fullWidth
                            disabled={subscriptionDialog.loading}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeSubscriptionDialog} disabled={subscriptionDialog.loading}>Annulla</Button>
                    <Button
                        variant="contained"
                        onClick={submitSubscriptionForm}
                        disabled={subscriptionDialog.loading}
                    >
                        {subscriptionDialog.loading ? 'Salvataggio...' : 'Salva'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Cancel Subscription Dialog */}
            <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Conferma cancellazione</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2">
                        Vuoi cancellare l&apos;abbonamento attivo di questo utente?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelDialogOpen(false)}>Annulla</Button>
                    <Button color="error" variant="contained" onClick={handleCancelSubscription}>
                        Cancella abbonamento
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
